import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { EDITION_STATUS, money, selectCls, btnBrand } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

const EMPTY = { start_date: "", end_date: "", start_time: "", end_time: "", location: "", capacity: "", normal_price: "", status: "open", notes: "", instructor_ids: [] };

// Fix U (2026-10-13): una fecha de un curso: dia, horario, lugar, cupo,
// instructores y el precio de cada semana (promo 1, promo 2 y normal).
export default function EditionModal({ open, onOpenChange, course, edition, instructors, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEdit = Boolean(edition);

  useEffect(() => {
    if (!open) return;
    setError("");
    if (edition) {
      setForm({
        start_date: edition.start_date || "", end_date: edition.end_date || "", start_time: edition.start_time || "",
        end_time: edition.end_time || "", location: edition.location || "", capacity: edition.capacity ? String(edition.capacity) : "",
        normal_price: edition.normal_price == null ? "" : String(edition.normal_price), status: edition.status || "open",
        notes: edition.notes || "", instructor_ids: edition.instructor_ids || [],
      });
      setTiers((edition.price_tiers || []).map((t) => ({ ...t, price: String(t.price) })));
    } else {
      setForm(EMPTY);
      setTiers([{ label: "Promo 1", price: "", until: "" }, { label: "Promo 2", price: "", until: "" }]);
    }
  }, [open, edition]);

  if (!course) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setTier = (i, k, v) => setTiers((ts) => ts.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  const toggleInstructor = (id) => setForm((f) => ({
    ...f, instructor_ids: f.instructor_ids.includes(id) ? f.instructor_ids.filter((x) => x !== id) : [...f.instructor_ids, id],
  }));

  const save = async () => {
    if (!form.start_date) {
      setError("Falta la fecha de inicio");
      return;
    }
    const cleanTiers = tiers.filter((t) => t.label.trim() && t.price !== "" && t.until)
      .map((t) => ({ label: t.label.trim(), price: Number(t.price), until: t.until }));
    setSaving(true);
    setError("");
    const body = {
      start_date: form.start_date, end_date: form.end_date || null, start_time: form.start_time || null, end_time: form.end_time || null,
      location: form.location.trim() || null, capacity: Number(form.capacity) || 0, instructor_ids: form.instructor_ids,
      price_tiers: cleanTiers, status: form.status, notes: form.notes.trim() || null,
    };
    if (form.normal_price !== "") body.normal_price = Number(form.normal_price);
    else if (isEdit) body.clear_normal_price = true;
    Object.keys(body).forEach((k) => body[k] === null && delete body[k]);
    try {
      const { data } = isEdit ? await api.put(`/academy/editions/${edition.id}`, body) : await api.post("/academy/editions", { ...body, course_id: course.id });
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid={ACADEMY.editionModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Editar fecha" : "Nueva fecha"} · {course.name}</DialogTitle>
          <DialogDescription className="sr-only">Fecha del curso</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Inicio</Label>
              <Input type="date" value={form.start_date} onChange={set("start_date")} data-testid={ACADEMY.editionStartInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Fin (si dura más de un día)</Label>
              <Input type="date" value={form.end_date} onChange={set("end_date")} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Hora de inicio</Label>
              <Input type="time" value={form.start_time} onChange={set("start_time")} data-testid={ACADEMY.editionStartTimeInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Hora de fin</Label>
              <Input type="time" value={form.end_time} onChange={set("end_time")} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Lugar</Label>
              <Input value={form.location} onChange={set("location")} placeholder="Ej: 6430 Richmond Ave, Oficina 270" data-testid={ACADEMY.editionLocationInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Cupo</Label>
              <Input type="number" min="0" value={form.capacity} onChange={set("capacity")} placeholder="Sin límite" data-testid={ACADEMY.editionCapacityInput} />
            </div>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Instructores</Label>
            {instructors.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nadie tiene el permiso de instructor todavía (Equipo → permisos en la academia).</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {instructors.map((i) => (
                  <label key={i.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.instructor_ids.includes(i.id)} onChange={() => toggleInstructor(i.id)} data-testid={`${ACADEMY.editionInstructorPrefix}${i.id}`} />
                    {i.name}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-lg border border-border p-3 space-y-3">
            <p className="text-sm font-medium">Precio de cada semana</p>
            <p className="text-xs text-muted-foreground">Se toma el primer precio cuya fecha "hasta" no pasó. Después, el precio normal.</p>
            {tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                <Input value={t.label} onChange={(e) => setTier(i, "label", e.target.value)} placeholder="Nombre" />
                <Input type="number" min="0" step="0.01" value={t.price} onChange={(e) => setTier(i, "price", e.target.value)} placeholder="Precio" data-testid={`${ACADEMY.editionTierPricePrefix}${i}`} />
                <Input type="date" value={t.until} onChange={(e) => setTier(i, "until", e.target.value)} data-testid={`${ACADEMY.editionTierUntilPrefix}${i}`} />
                <Button type="button" variant="ghost" size="icon" onClick={() => setTiers((ts) => ts.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setTiers((ts) => [...ts, { label: `Promo ${ts.length + 1}`, price: "", until: "" }])}><Plus className="w-4 h-4" /> Agregar precio</Button>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Precio normal de esta fecha</Label>
              <Input type="number" min="0" step="0.01" value={form.normal_price} onChange={set("normal_price")} placeholder={`${money(course.base_price)} (el del curso)`} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Estado</Label>
              <select className={selectCls} value={form.status} onChange={set("status")} data-testid={ACADEMY.editionStatusSelect}>
                {Object.entries(EDITION_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Notas</Label>
              <Input value={form.notes} onChange={set("notes")} />
            </div>
          </div>
          {error && <p className="text-sm text-destructive" data-testid={ACADEMY.modalError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className={btnBrand} onClick={save} disabled={saving} data-testid={ACADEMY.editionSaveButton}>{saving ? "Guardando..." : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
